/**
 * Module dependencies.
 */

import { Check } from 'lucide-react';
import React from 'react';

/**
 * Password rules (kept in sync with the backend policy).
 */

export const passwordRules: { label: string; test: (value: string) => boolean }[] = [
  { label: 'At least 8 characters', test: value => value.length >= 8 },
  { label: 'One uppercase letter', test: value => /[A-Z]/.test(value) },
  { label: 'One lowercase letter', test: value => /[a-z]/.test(value) },
  { label: 'One number', test: value => /[0-9]/.test(value) },
  { label: 'One special character', test: value => /[^A-Za-z0-9]/.test(value) }
];

/**
 * Whether a password meets every rule.
 */

export function allPasswordRulesMet(value: string): boolean {
  return passwordRules.every(rule => rule.test(value));
}

/**
 * Requirement row.
 */

function Requirement({ label, met }: { label: string; met: boolean }): JSX.Element {
  return (
    <div className={'flex items-center gap-2'}>
      <span
        className={'grid h-4 w-4 shrink-0 place-items-center rounded-full transition-colors'}
        style={{ backgroundColor: met ? '#1B752B' : 'rgba(10,10,11,0.08)' }}
      >
        {met && <Check className={'h-2.5 w-2.5 text-white'} strokeWidth={3} />}
      </span>

      <span className={met ? 'text-neutral-600' : 'text-neutral-400'}>{label}</span>
    </div>
  );
}

/**
 * Export `PasswordRequirements` — live checklist.
 */

export function PasswordRequirements({ value }: { value: string }): JSX.Element {
  return (
    <div className={'grid grid-cols-1 gap-y-1.5 pt-0.5 text-xs sm:grid-cols-2 sm:gap-x-4'}>
      {passwordRules.map(rule => (
        <Requirement key={rule.label} label={rule.label} met={rule.test(value)} />
      ))}
    </div>
  );
}
